import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Bryter } from '../kjentefeil/Bryter.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';

/**
 * Raden er bærebjelken på Innstillinger og Organisasjon (R5.3, `admin-innstillinger.jsx`):
 * navnet, verdien i ÉN lesbar linje, og «Endre» — eller «Vis» der det som ligger bak har
 * egen lagring eller bare er visning, eller «Låst» der K18 tvinger verdien.
 *
 * Verdilinjen utledes av SAMME tilstand som feltene i den åpnede raden, aldri av en egen
 * streng: en lukket rad viser fortsatt verdien som tekst, så nettleserens Ctrl+F finner
 * verdien og ikke feltetiketten. Det er det trekkspillene gjorde, uten trekkspillene
 * («Innstillinger er to sider med ett utkast per side», tech-decisions.md).
 *
 * «Endre» åpner raden PÅ STEDET: feltene står der verdien sto, og «Lukk» (Esc) lukker den
 * igjen — verdien ligger i utkastet til foten lagrer eller forkaster. Ingen «angre» per rad:
 * `sett(felt, undefined)` lar nøkkelen stå og ville telt som en endring.
 */

interface Radstyring {
  /** Id-en til raden som står åpen. Én om gangen: å åpne en ny lukker den forrige. */
  aapen: string | null;
  aapne: (id: string | null) => void;
  /** «Vis tekniske navn» (DD-50 P4): kontraktens feltnavn finnes i DOM bare når denne er på. */
  tekniske: boolean;
  /** Eier. Andre roller ser alt, men «Endre» finnes ikke for dem (DD-01). */
  kanEndre: boolean;
}

const Ctx = createContext<Radstyring>({ aapen: null, aapne: () => undefined, tekniske: false, kanEndre: false });

export const RadProvider = Ctx.Provider;

export function useRadstyring(kanEndre: boolean): Radstyring & { setTekniske: (v: boolean) => void } {
  const [aapen, setAapen] = useState<string | null>(null);
  // Lesehjelp, ikke en innstilling: valget lagres ikke (vakt 1 står, og det har ingen tast).
  const [tekniske, setTekniske] = useState(false);

  const aapne = useCallback((id: string | null) => setAapen((f) => (id === null || f === id ? null : id)), []);

  // Esc lukker den åpne raden, også fra et felt. En dialog stopper tasten før den kommer
  // hit (Dialog.tsx lytter i capture-fasen), så Esc i en dialog lukker bare dialogen.
  useEffect(() => {
    if (aapen === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setAapen(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [aapen]);

  return useMemo(() => ({ aapen, aapne, tekniske, kanEndre, setTekniske }), [aapen, aapne, tekniske, kanEndre]);
}

export function useRad(): Radstyring {
  return useContext(Ctx);
}

/**
 * Feltkoden: `small` under radnavnet, og bare når «Vis tekniske navn» er på — ellers ikke i
 * DOM (DD-50 presisert 2026-09-20; DD-67: `tiny` finnes bare i piller og `kbd`).
 */
export function Kode({ children }: { children: ReactNode }) {
  return <span className="panel-innst-kode">{children}</span>;
}

/** Bryteren for feltkodene. Samme etikett i begge bredder, og ingen tast (avvik 13). */
export function TekniskeBryter({ paa, onChange }: { paa: boolean; onChange: (v: boolean) => void }) {
  return (
    <span className="panel-innst-tekniske">
      <Bryter label="Vis tekniske navn" paa={paa} onChange={onChange} testid="tekniske" />
    </span>
  );
}

/** Seksjonen er en overskrift og radene under — ingen ramme, ingen `<details>`, ingen åpne-tilstand. */
export function Seksjon({ id, tittel, children }: { id: string; tittel: string; children: ReactNode }) {
  return (
    <section aria-labelledby={`sek-${id}`} className="panel-innst-seksjon" data-testid={`seksjon-${id}`}>
      <h2 id={`sek-${id}`} className="panel-innst-seksjonstittel">
        {tittel}
      </h2>
      {children}
    </section>
  );
}

export interface RadProps {
  id: string;
  navn: string;
  /** Kontraktens feltnavn. Vises bare bak «Vis tekniske navn». */
  kode?: string;
  verdi: ReactNode;
  /** Kortere verdi under 900 px, der raden er to linjer (konseptets skjerm 6). */
  verdiSmal?: ReactNode;
  handling: 'Endre' | 'Vis' | 'Åpne' | 'Velg i Organisasjon' | null;
  /**
   * «Åpne» går til en annen side og åpner ingenting her (DD-68 presisert 2026-09-25:
   * Panelbrukere er en egen side under Organisasjon). Da er handlingen en lenke hit. Med «Endre»
   * er det en underside som skriver til samme utkast (rommet Hypoteser, H2.3).
   */
  til?: string;
  endret?: boolean;
  /** K18 tvinger verdien: «Låst» står der «Endre» sto, med hengelås OG ordet (DD-08). */
  laast?: boolean;
  children?: ReactNode;
}

export function Rad({ id, navn, kode, verdi, verdiSmal, handling, til, endret, laast, children }: RadProps) {
  const { aapen, aapne, tekniske, kanEndre } = useRad();
  const smal = useSmal();
  const apen = aapen === id;
  // «Vis» er lesing og står for alle roller; «Endre» finnes ikke for behandler og leser.
  const vis = laast ? null : handling === 'Endre' && !kanEndre ? null : handling;

  return (
    <div className="panel-rad-innst" data-testid={`rad-${id}`} data-endret={endret ? 'true' : undefined}>
      <span className="panel-rad-innst-navn">
        <span className="panel-rad-innst-navn-tekst">
          {navn}
          {endret ? (
            <span className="panel-innst-merke-endret">
              <Ikon navn="create-outline" />
              <span>endret</span>
            </span>
          ) : null}
        </span>
        {tekniske && kode ? <Kode>{kode}</Kode> : null}
      </span>
      <span className="panel-rad-innst-verdi" data-testid={`rad-verdi-${id}`}>
        {smal && verdiSmal !== undefined ? verdiSmal : verdi}
      </span>
      <span className="panel-rad-innst-handling">
        {til && (handling === 'Åpne' || handling === 'Velg i Organisasjon' || (vis && !laast)) ? (
          // «Endre» med `til` åpner en underside med samme utkast (rommet Hypoteser: ordlyd og koder).
          <Link to={til} className="panel-knapp-lenke" aria-label={`${handling} ${navn}`} data-testid={`rad-handling-${id}`}>
            {/* «Endre» står i samme kolonne som knappene som åpner på stedet: pilen foran, som dem. */}
            {handling === 'Endre' ? <Ikon navn="chevron-forward-outline" className="panel-innst-pil" /> : null}
            {handling}
            {handling === 'Åpne' || handling === 'Velg i Organisasjon' ? <Ikon navn="chevron-forward-outline" className="panel-innst-pil" /> : null}
          </Link>
        ) : laast ? (
          <span className="panel-innst-laast">
            <Ikon navn="lock-closed-outline" /> Låst
          </span>
        ) : vis ? (
          <button
            type="button"
            className="panel-knapp-lenke"
            aria-expanded={apen}
            aria-controls={apen ? `rad-panel-${id}` : undefined}
            // «Endre» står elleve ganger på siden: navnet sier hvilken rad knappen gjelder.
            aria-label={apen ? `Lukk ${navn}` : `${vis} ${navn}`}
            onClick={() => aapne(id)}
            data-testid={`rad-handling-${id}`}
          >
            {/* Ett ikon, rotert av CSS når raden er åpen — panelet har ingen «chevron-down». */}
            <Ikon navn="chevron-forward-outline" className={apen ? 'panel-innst-pil panel-innst-pil-apen' : 'panel-innst-pil'} />
            {apen ? 'Lukk' : vis}
          </button>
        ) : null}
      </span>
      {apen ? (
        <div className="panel-rad-innst-panel" id={`rad-panel-${id}`} data-testid={`rad-panel-${id}`}>
          {children}
          <div>
            {/* «Lukk» lukker uten å lagre — utkastet står til foten lagrer eller forkaster (DD-60). */}
            <button type="button" className="panel-knapp-lenke" onClick={() => aapne(null)} data-testid={`rad-lukk-${id}`} aria-label={`Lukk ${navn}`}>
              Lukk<kbd>Esc</kbd>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
